<template>
  <div>
    <v-toolbar color="page-toolbar">
      <v-toolbar-title class="text-body-large"> Mon profil </v-toolbar-title>
    </v-toolbar>

    <v-row>
      <v-col cols="12"></v-col>
      <v-col cols="12" md="8" lg="6">
        <v-card class="mt-4 ml-2 mr-2" elevation="6">
          <v-card-title>
            <h3 class="text-headline-small">Mon nom</h3>
          </v-card-title>
          <v-card-text>
            <div v-if="!user" class="text-medium-emphasis">Connectez-vous pour voir et modifier votre nom.</div>

            <div v-else>
              <v-form ref="nameForm">
                <v-text-field
                  v-model="nameForm.displayName"
                  :disabled="loading || savingName"
                  :counter="50"
                  :rules="displayNameRules"
                  label="Votre nom"
                  required
                />

                <v-btn color="primary" :loading="savingName" :disabled="loading || savingName" @click="saveDisplayName">
                  Enregistrer mon nom
                </v-btn>
              </v-form>
            </div>
          </v-card-text>
        </v-card>

        <v-card class="mt-4 ml-2 mr-2" elevation="6">
          <v-card-title>
            <h3 class="text-headline-small">Préférences d'affichage</h3>
          </v-card-title>
          <v-card-text>
            <div class="mb-6">
              <div class="text-body-medium mb-2">Thème</div>
              <theme-selector></theme-selector>
              <div class="text-body-small mt-2">
                Appliqué immédiatement et retenu sur cet appareil. «&nbsp;Appareil&nbsp;» suit le réglage clair ou sombre de
                votre téléphone ou ordinateur.
              </div>
            </div>

            <div class="mb-6">
              <div class="text-body-medium mb-2">Nom du site</div>
              <site-name-selector></site-name-selector>
              <div class="text-body-small mt-2">
                Nom affiché dans la barre du haut et à l'accueil. Appliqué immédiatement et retenu sur cet appareil.
              </div>
            </div>

            <div v-if="!user" class="text-medium-emphasis">Connectez-vous pour configurer vos préférences.</div>

            <div v-else>
              <v-switch
                v-model="form.sectionDataTableRegionsExpandedByDefault"
                :disabled="loading || saving"
                color="primary"
                label="Rivières: régions dépliées par défaut"
                persistent-hint
                hint="Désactivez pour ouvrir la liste des rivières avec les régions repliées"
              />


              <v-switch
                v-model="form.partnerCarouselExpandedByDefault"
                :disabled="loading || saving || !canConfigurePartnerCarouselPreference"
                color="primary"
                label="Carrousel partenaires: ouvert par défaut"
                persistent-hint
                :hint="
                  canConfigurePartnerCarouselPreference
                    ? 'Désactivez pour garder le carrousel partenaires replié au chargement'
                    : 'Option réservée aux membres Premium'
                "
                class="mb-6"
              />

              <v-btn color="primary" :loading="saving" :disabled="loading || saving" @click="savePreferences">
                Enregistrer
              </v-btn>
            </div>

            <v-alert v-if="successMessage" type="success" class="mt-4" density="compact">
              {{ successMessage }}
            </v-alert>
            <v-alert v-if="errorMessage" type="error" class="mt-4" density="compact">
              {{ errorMessage }}
            </v-alert>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
  import { auth } from "@/firebase";
  import { UserService } from "@/store/modules/user";
  import { notify, notifyError } from "@/services/Notification.service";
  import ThemeSelector from "@/components/ThemeSelector.vue";
  import SiteNameSelector from "@/components/SiteNameSelector.vue";

  const defaultPreferences = {
    likedSectionsTableRegionsExpandedByDefault: true,
    sectionDataTableRegionsExpandedByDefault: true,
    partnerCarouselExpandedByDefault: true,
  };

  export default {
    name: "MyProfile",
    components: { ThemeSelector, SiteNameSelector },
    props: {
      isMember: {
        type: Boolean,
        default: false,
      },
      isAdmin: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        user: null,
        loading: true,
        saving: false,
        savingName: false,
        successMessage: "",
        errorMessage: "",
        nameForm: {
          displayName: "",
        },
        displayNameRules: [
          (v) => !!v || "Un nom d'utilisateur est requis",
          (v) => (v && v.trim().length > 2) || "Le nom d'utilisateur doit contenir au moins 3 caractères",
        ],
        form: { ...defaultPreferences },
        unsubscribeAuth: null,
      };
    },
    computed: {
      canConfigurePartnerCarouselPreference() {
        return this.isMember || this.isAdmin;
      },
    },
    mounted() {
      this.unsubscribeAuth = auth.onAuthStateChanged(async (user) => {
        this.user = user;
        this.successMessage = "";
        this.errorMessage = "";

        if (!user || !user.uid) {
          this.nameForm.displayName = "";
          this.form = { ...defaultPreferences };
          this.loading = false;
          return;
        }

        this.loading = true;
        try {
          this.nameForm.displayName = user.displayName || "";
          const preferences = await UserService.actions.getUserUiPreferencesByUid(user.uid);
          this.form = {
            likedSectionsTableRegionsExpandedByDefault: Boolean(preferences.likedSectionsTableRegionsExpandedByDefault),
            sectionDataTableRegionsExpandedByDefault: Boolean(preferences.sectionDataTableRegionsExpandedByDefault),
            partnerCarouselExpandedByDefault: Boolean(preferences.partnerCarouselExpandedByDefault),
          };

          if (!this.canConfigurePartnerCarouselPreference) {
            this.form.partnerCarouselExpandedByDefault = true;
          }
        } catch {
          this.errorMessage = "Impossible de charger vos préférences pour le moment.";
          this.form = { ...defaultPreferences };
        } finally {
          this.loading = false;
        }
      });
    },
    beforeUnmount() {
      if (this.unsubscribeAuth) {
        this.unsubscribeAuth();
        this.unsubscribeAuth = null;
      }
    },
    methods: {
      async saveDisplayName() {
        if (!this.user) {
          this.errorMessage = "Vous devez être connecté pour modifier votre nom.";
          return;
        }

        if (this.$refs.nameForm) {
          const { valid } = await this.$refs.nameForm.validate();
          if (!valid) {
            return;
          }
        }

        const trimmedDisplayName = (this.nameForm.displayName || "").trim();
        if (trimmedDisplayName.length < 3) {
          this.errorMessage = "Le nom d'utilisateur doit contenir au moins 3 caractères.";
          return;
        }

        this.savingName = true;
        this.successMessage = "";
        this.errorMessage = "";

        try {
          await this.user.updateProfile({ displayName: trimmedDisplayName });
          this.nameForm.displayName = trimmedDisplayName;
          this.successMessage = "Nom mis à jour.";
          notify("Votre nom a été mis à jour.");
        } catch {
          this.errorMessage = "Impossible de mettre à jour votre nom pour le moment.";
          notifyError("Impossible de mettre à jour votre nom.");
        } finally {
          this.savingName = false;
        }
      },
      async savePreferences() {
        if (!this.user || !this.user.uid) {
          this.errorMessage = "Vous devez être connecté pour enregistrer vos préférences.";
          return;
        }

        this.saving = true;
        this.successMessage = "";
        this.errorMessage = "";

        try {
          await UserService.actions.setUserUiPreferences(this.user.uid, {
            likedSectionsTableRegionsExpandedByDefault: Boolean(this.form.likedSectionsTableRegionsExpandedByDefault),
            sectionDataTableRegionsExpandedByDefault: Boolean(this.form.sectionDataTableRegionsExpandedByDefault),
            partnerCarouselExpandedByDefault: this.canConfigurePartnerCarouselPreference
              ? Boolean(this.form.partnerCarouselExpandedByDefault)
              : true,
          });
          this.successMessage = "Préférences enregistrées.";
          notify("Préférences du profil enregistrées.");
        } catch {
          this.errorMessage = "Échec de l'enregistrement. Veuillez réessayer.";
          notifyError("Impossible d'enregistrer les préférences du profil.");
        } finally {
          this.saving = false;
        }
      },
    },
  };
</script>
